<!--
  Picks an element of the page: the element under the pointer is outlined,
  a click (or tap) picks it, Esc cancels. Changeling's feedback controls
  are never picked.
-->
<script lang="ts">
  import { describeElement } from '../feedback/picker';
  import type { PickedElement } from '../feedback/report';
  import { IGNORE_ATTR } from '../feedback/capture';

  interface Props {
    onpick: (picked: PickedElement) => void;
    oncancel: () => void;
  }
  let { onpick, oncancel }: Props = $props();
  let box: { left: number; top: number; width: number; height: number; label: string } | null = $state(null);

  function target(e: { clientX: number; clientY: number }): Element | null {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el || el.closest(`[${IGNORE_ATTR}]`) || el === document.body || el === document.documentElement) return null;
    return el;
  }

  function onmove(e: PointerEvent) {
    const el = target(e);
    if (!el) {
      box = null;
      return;
    }
    const r = el.getBoundingClientRect();
    const name = el.tagName.toLowerCase() + [...el.classList].slice(0, 2).map((c) => '.' + c).join('');
    box = { left: r.left, top: r.top, width: r.width, height: r.height, label: name };
  }

  function onclick(e: MouseEvent) {
    const el = target(e);
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    onpick(describeElement(el));
  }

  function onkeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      oncancel();
    }
  }

  $effect(() => {
    // Capture phase, so the page's own handlers (links, buttons) never see the pick.
    document.addEventListener('pointermove', onmove, true);
    document.addEventListener('click', onclick, true);
    document.addEventListener('keydown', onkeydown, true);
    return () => {
      document.removeEventListener('pointermove', onmove, true);
      document.removeEventListener('click', onclick, true);
      document.removeEventListener('keydown', onkeydown, true);
    };
  });
</script>

<div class="picker-bar" data-feedback-ignore role="status">
  <span>Click the part of the page your feedback is about.</span>
  <button class="btn small" onclick={oncancel}>Cancel · Esc</button>
</div>
{#if box}
  <div class="picker-box" data-feedback-ignore aria-hidden="true" style="left:{box.left}px;top:{box.top}px;width:{box.width}px;height:{box.height}px">
    <span>{box.label}</span>
  </div>
{/if}

<style>
  .picker-bar {
    position: fixed;
    z-index: 1001;
    left: 50%;
    bottom: 20px;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 10px 14px;
    border-radius: 10px;
    background: var(--nav);
    color: var(--navfg);
    box-shadow: 0 10px 30px #0004;
    font-size: 13px;
    max-width: calc(100% - 32px);
  }
  .picker-box {
    position: fixed;
    z-index: 1000;
    pointer-events: none;
    outline: 2px solid #e5484d;
    background: #e5484d1f;
    border-radius: 2px;
  }
  .picker-box span {
    position: absolute;
    left: -2px;
    bottom: 100%;
    background: #e5484d;
    color: #fff;
    font: 11px ui-monospace, monospace;
    padding: 1px 5px;
    white-space: nowrap;
    max-width: 60vw;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
